/**
 * api-client.ts — talking to the API (P1 US-113, FR-22, FR-23, FR-24, FR-30).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THIS FILE OWNS THE MOST CONSEQUENTIAL BEHAVIOUR CHANGE IN THE WHOLE PORT
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The oracle binds `input` AND `change` on every control to `calculer(mod)` (lines 851–852). It
 * recalculates on every keystroke, and that was free: the engine was in the page, a calc is a few
 * microseconds, and nothing could arrive out of order because nothing was asynchronous.
 *
 * Ported verbatim, that is one HTTP request per character typed. Three separate problems:
 *
 *  1. **Cost.** Every request is a metered cost unit and a CPU slice on a Worker whose CPU budget
 *     is the binding constraint the P0 spike identified.
 *  2. **Ordering.** Requests can complete out of order. The naive version — render whatever
 *     arrives — means a user types `4500`, and the answer for `450` lands last and stays on
 *     screen. **A stale result in a structural verification tool is worse than no result**, because
 *     it looks exactly like a fresh one and an engineer has no way to tell.
 *  3. **Cold start.** The oracle calculates all three modules at boot (line 1238), including the
 *     two hidden panes. Preserving that triples cold-start cost for two answers nobody is looking
 *     at.
 *
 * The three defences below — debounce, abort, sequence numbers — are why this is the highest-risk
 * story in Track C. Not because any one is hard, but because getting the third subtly wrong
 * produces a UI that is confidently wrong, and that is the failure this product cannot have.
 */

import {
  CalcResponseSchema,
  OptimiseResponseSchema,
  type CalcResponse,
  type OptimiseRequestInput,
  type OptimiseResponse,
} from '@ronceray/contract';

import { API_BASE_URL, CALC_DEBOUNCE_MS } from './config';

/** What a call can go wrong as. Discriminated so US-114 renders cases, not status codes. */
export type ApiFailureKind =
  /** 422 — the request was not a valid calculation input. Per-field messages in `body`. */
  | 'invalid'
  /** 404 — a catalog name the API does not have, an unknown route, or a resource that is not the
   * caller's (P2/US-203: not found, never forbidden). */
  | 'unknown'
  /** 429 — burst limit or monthly quota. */
  | 'quota'
  /**
   * 401 — no session, or an expired one (P2/US-204).
   *
   * A kind of its own rather than folded into `server`, because it is the one failure with a
   * **remedy the UI can offer**: sign in again. Rendering "erreur du serveur" for an expired
   * session would leave an engineer reloading a page that will never work.
   */
  | 'auth'
  /** The request never completed: offline, DNS, CORS, timeout. */
  | 'network'
  /** 5xx, or a response that did not parse against the frozen schema. */
  | 'server';

export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly kind: ApiFailureKind; readonly body: unknown };

/** A superseded request resolves to this rather than rejecting. */
export const SUPERSEDED = Symbol('superseded');

function kindForStatus(status: number): ApiFailureKind {
  if (status === 401) return 'auth';
  if (status === 422) return 'invalid';
  if (status === 404) return 'unknown';
  if (status === 429) return 'quota';
  return 'server';
}

/**
 * One request, with the response validated before anybody looks at it.
 *
 * **Validation is not defensive programming here, it is the contract doing its job** (FR-24). The
 * envelope shape is frozen and the browser has the schema; a response that does not match it means
 * the API and the client disagree about the shape of an engineering result, and rendering half of
 * it would put numbers on screen under labels that may not belong to them. A parse failure is a
 * rendered error, never a silent half-render.
 */
export interface RequestOptions<T> {
  readonly method?: 'GET' | 'POST' | 'DELETE';
  /** Omitted entirely for a GET or a DELETE — a body on those is a request nothing will read. */
  readonly body?: unknown;
  readonly parse: (value: unknown) => T;
  readonly signal?: AbortSignal;
}

/**
 * The one HTTP call in the browser (P2/US-212 generalised it from POST-only).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `credentials: 'include'` IS WHAT MAKES THE BETA WORK AT ALL AFTER WAVE 2
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * P2/US-204 put a session in front of every route except three. The session lives in a cookie the
 * API sets, and a cross-origin `fetch` **does not send cookies unless asked** — the browser and
 * the API are different origins even locally (`:4173` and `:8787`). Without this flag every call
 * from the built application returns 401, which is precisely what wave 2 left behind and what the
 * unit suites could not see: they call the Hono app in-process, where there is no browser to
 * withhold anything.
 *
 * Its counterpart is on the server — `credentials: true` in `apps/api/src/middleware/cors.ts`.
 * Neither half works alone, and the only thing that exercises the pair is the Playwright suite.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions<T>,
): Promise<ApiResult<T>> {
  const method = options.method ?? 'POST';
  const sendsBody = method === 'POST' && options.body !== undefined;
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: sendsBody ? { 'Content-Type': 'application/json' } : {},
      body: sendsBody ? JSON.stringify(options.body) : null,
      credentials: 'include',
      signal: options.signal ?? null,
    });
  } catch (error) {
    // An abort lands here too. The caller distinguishes it; see `calcClient` below.
    if (options.signal?.aborted === true) throw error;
    return { ok: false, kind: 'network', body: error };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { ok: false, kind: response.ok ? 'server' : kindForStatus(response.status), body: null };
  }

  if (!response.ok) {
    return { ok: false, kind: kindForStatus(response.status), body: payload };
  }

  try {
    return { ok: true, value: options.parse(payload) };
  } catch (error) {
    // A 200 whose body does not match the frozen schema is a server problem, not a user one.
    return { ok: false, kind: 'server', body: error };
  }
}

/** One calculation. Exported for callers that want a single shot with no debouncing. */
export function calcRequest(
  module: string,
  input: unknown,
  signal?: AbortSignal,
): Promise<ApiResult<CalcResponse>> {
  return apiRequest(`/v1/calc/${module}`, {
    body: input,
    parse: (v) => CalcResponseSchema.parse(v),
    ...(signal === undefined ? {} : { signal }),
  });
}

/** One optimiser sweep. Never debounced: it is triggered by a button, not by typing. */
export function optimiseRequest(
  body: OptimiseRequestInput,
  signal?: AbortSignal,
): Promise<ApiResult<OptimiseResponse>> {
  return apiRequest('/v1/optimise', {
    body,
    parse: (v) => OptimiseResponseSchema.parse(v),
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * A per-module calculation client with debounce, abort and ordering.
 *
 * Three defences, and each covers a case the other two do not:
 *
 *  - **Debounce** collapses a burst of keystrokes into one request. It reduces cost; it does not
 *    make ordering safe, because two settled edits 300 ms apart still produce two in-flight
 *    requests.
 *  - **Abort** cancels the superseded request. It saves the server work; it does not make ordering
 *    safe either, because `AbortController` cannot un-send a request that has already been
 *    answered and is sitting in the event loop.
 *  - **The sequence number** is what actually guarantees correctness. Every request carries a
 *    monotonic number; a response whose number is not the newest is discarded without touching the
 *    screen. This is the one that stops a stale answer being displayed as a fresh one.
 */
export function calcClient(options: { debounceMs?: number } = {}) {
  const debounceMs = options.debounceMs ?? CALC_DEBOUNCE_MS;

  /**
   * A calculation that has been scheduled but not yet sent.
   *
   * The `resolve` is kept alongside the timer, and that is not bookkeeping for its own sake: when
   * a newer edit supersedes a pending one, clearing the timer alone would leave the older promise
   * **permanently unsettled**. A caller that awaited it — a UI showing a "computing" state, say —
   * would wait forever, on every keystroke but the last. Superseded work must resolve, not vanish.
   */
  interface Scheduled {
    readonly timer: ReturnType<typeof setTimeout>;
    readonly settle: (value: typeof SUPERSEDED) => void;
  }

  const scheduled = new Map<string, Scheduled>();
  const controllers = new Map<string, AbortController>();
  const latest = new Map<string, number>();
  let sequence = 0;

  return {
    /**
     * Schedule a calculation. Resolves with the result, or with `SUPERSEDED` if a newer request
     * for the same module took its place — never hangs, and never rejects.
     */
    calculate(
      module: string,
      input: unknown,
    ): Promise<ApiResult<CalcResponse> | typeof SUPERSEDED> {
      const pending = scheduled.get(module);
      if (pending !== undefined) {
        clearTimeout(pending.timer);
        pending.settle(SUPERSEDED);
        scheduled.delete(module);
      }

      controllers.get(module)?.abort();

      const ticket = (sequence += 1);
      latest.set(module, ticket);

      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          scheduled.delete(module);

          const controller = new AbortController();
          controllers.set(module, controller);

          calcRequest(module, input, controller.signal)
            .then((result) => {
              // The ordering guarantee, checked AFTER the await — the only moment at which "is
              // this still the newest" can be answered.
              resolve(latest.get(module) === ticket ? result : SUPERSEDED);
            })
            .catch(() => {
              // An abort lands here. So does anything else that escaped `request`.
              resolve(
                latest.get(module) === ticket
                  ? { ok: false, kind: 'network', body: null }
                  : SUPERSEDED,
              );
            })
            .finally(() => {
              if (controllers.get(module) === controller) controllers.delete(module);
            });
        }, debounceMs);

        scheduled.set(module, { timer, settle: resolve });
      });
    },

    /** Cancel everything in flight. For teardown, and for tests. */
    cancelAll(): void {
      for (const [module, entry] of scheduled) {
        clearTimeout(entry.timer);
        entry.settle(SUPERSEDED);
        scheduled.delete(module);
      }
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
    },
  };
}

/**
 * A human sentence for a failure, for the panel US-114 renders.
 *
 * The oracle had exactly one non-result state: `<p class="vide">Saisie incomplete.</p>`. The beta
 * needs more, and they must be **distinguishable** — "the server cannot be reached" and "this
 * input is invalid" call for completely different actions from the user, and a single generic
 * message would leave an engineer refreshing the page over a typo.
 */
export function failureMessage(kind: ApiFailureKind): string {
  switch (kind) {
    case 'invalid':
      return 'Saisie incomplete.';
    case 'unknown':
      return 'Section ou nuance inconnue du catalogue.';
    case 'quota':
      return 'Quota atteint. Reessayez plus tard.';
    case 'auth':
      return 'Session expiree. Reconnectez-vous.';
    case 'network':
      return 'Serveur injoignable. Verifiez la connexion.';
    case 'server':
      return 'Erreur du serveur. Le calcul n a pas pu etre effectue.';
  }
}
